/**
 * @fileoverview Texture swatch panel helpers and background tint sync.
 */
'use strict'

import SwatchDataCache from './SwatchDataCache'
import { DOM } from './DOM'
import { Selector } from './Selectors'
import { buildTextureSwatchPagesData } from './SwatchPanelDataBuilder.js'

/** @type {boolean} */
let isDropcolorSyncBound = false

/**
 * TextureSwatchPanel
 * Handles the texture-swatch selector (material/effect dataset) for the right panel.
 * Responsibilities:
 * - Load the selected texture-swatch dataset from SwatchDataCache.
 * - Update swatch selection state for texture-swatch datasets.
 * - Render the texture-swatch grid.
 * - Emit viz:swatchDatasetLoaded for other UI modules (search, etc).
 */
/**
 * Update texture-swatch tiles with the selected color.
 *
 * @param {Object} params
 * @param {number} params.r
 * @param {number} params.g
 * @param {number} params.b
 * @param {string|number} params.swatch_color_id
 */
export const updateTextureSwatchBackgroundColor = ({ r, g, b, swatch_color_id }) => {
    console.log('updateTextureSwatchBackgroundColor')

    $('.swatchDroppableSelected').removeClass('swatchDroppableSelected')
    $('#' + swatch_color_id).addClass('swatchDroppableSelected')

    const container = DOM.get(Selector.textureswatchselectors)
    if (!container) return
    container.querySelectorAll('.texture-swatch').forEach(function (swatchEl) {
        const el = document.getElementById(swatchEl.id)
        if (!el) return

        el.style.backgroundColor = 'rgb(' + r + ',' + g + ',' + b + ')'
        el.setAttribute('colorid', swatch_color_id)
        el.setAttribute('textureswatchid', swatchEl.id)

        DOM.show(Selector.textureswatchselectors)
        DOM.show(Selector.scrollerEffects)
    })
}

/**
 * Bind dropcolor events so texture swatches stay tinted with the active color.
 *
 * @param {Object} app
 * @returns {void}
 */
export const bindTextureSwatchBackgroundSync = (app) => {
    if (isDropcolorSyncBound) return
    isDropcolorSyncBound = true

    /**
     * Apply tint from a Color payload if possible.
     * @param {Object} color
     */
    const updateFromColor = (color) => {
        const sw = color?.swatch_color
        if (!sw || !app?.swatchSelection?.currentTextureSwatch) return
        updateTextureSwatchBackgroundColor({
            r: sw.sr,
            g: sw.sg,
            b: sw.sb,
            swatch_color_id: sw.id,
        })
    }

    /**
     * Clear tint + selection when no dropcolor is active.
     */
    const clearTint = () => {
        document.querySelectorAll('.swatchDroppableSelected').forEach((el) => el.classList.remove('swatchDroppableSelected'))
        const container = DOM.get(Selector.textureswatchselectors)
        if (!container) return
        container.querySelectorAll('.texture-swatch').forEach((swatchEl) => {
            const el = document.getElementById(swatchEl.id)
            if (!el) return
            el.style.backgroundColor = ''
            el.removeAttribute('colorid')
        })
    }

    document.addEventListener('viz:dropcolorSelected', (event) => {
        updateFromColor(event?.detail?.color)
    })

    document.addEventListener('viz:dropcolorEmptySelected', () => {
        clearTint()
    })

    document.addEventListener('viz:dropcolorColorUpdated', (event) => {
        const slot = event?.detail?.slot
        const activeSlot = app?.canvasState?.colorSample?.workingColorPos
        if (slot && activeSlot && Number(slot) !== Number(activeSlot)) return
        updateFromColor(event?.detail?.color)
    })
}

/**
 * Ensure there is a selected swatch color and update texture-swatch previews.
 *
 * @param {Object} app
 * @returns {void}
 */
export const applyDefaultSwatchSelection = (app) => {
    const selection = app?.swatchSelection
    if (!selection || selection.hasSwatchTextureFilenames()) return

    const selectedId = document.querySelector('.swatchDroppableSelected')?.id || null

    const findColor = (id) => {
        for (const page of selection.currentSwatch?.swatch_pages || []) {
            for (const color of page?.swatch_colors || []) {
                if (String(color.id) === String(id)) return color
            }
        }
        return null
    }

    const color =
        (selectedId && findColor(selectedId)) ||
        selection.currentSwatch?.swatch_pages?.[0]?.swatch_colors?.[0]

    if (!color) return

    updateTextureSwatchBackgroundColor({
        r: color.sr,
        g: color.sg,
        b: color.sb,
        swatch_color_id: color.id,
    })
}

/**
 * Render the texture-swatch grid for the current selection.
 *
 * @param {Object} app
 * @returns {void}
 */
export const renderTextureSwatchSelector = (app) => {
    const selection = app?.swatchSelection
    if (!selection?.currentTextureSwatch) return

    const viewModel = buildTextureSwatchPagesData(selection.currentTextureSwatch)
    app.swatchUiAdapter.renderTextureSwatches({
        viewModel,
        currentTextureSwatch: selection.currentTextureSwatch,
    })

    app?.canvasState?.colorSample?.updateColorInfos?.()
    applyDefaultSwatchSelection(app)
    bindTextureSwatchBackgroundSync(app)
    app.swatchUiAdapter.initDragAndDrop()
}

class TextureSwatchPanel {
    /**
     * @param {Object} params
     * @param {Object} params.app - Application instance (holds swatchSelection, canvasState, etc).
     * @param {HTMLSelectElement} params.selectEl - <select id="selectte"> element.
     */
    constructor({ app, selectEl }) {
        this.app = app
        this.selectEl = selectEl
        this.dataCache = SwatchDataCache.getInstance()
    }

    /**
     * Bind the change handler to the texture-swatch selector.
     * @returns {void}
     */
    bind() {
        if (!this.selectEl) return
        this.selectEl.addEventListener('change', (event) => {
            this.#handleChange(event)
        })
    }

    async #handleChange(event) {
        const app = this.app
        const textureSwatchId = event?.target?.value
        if (!textureSwatchId) return

        const textureSwatch = await this.dataCache.getTextureSwatchOrFetch(textureSwatchId)
        app.swatchSelection.currentTextureSwatch = textureSwatch
        app.swatchSelection.currentTextureSwatchId = textureSwatch.id
        renderTextureSwatchSelector(app)

        document.dispatchEvent(
            new CustomEvent('viz:swatchDatasetLoaded', {
                detail: { type: 'textureSwatch', id: textureSwatch.id },
            }),
        )
    }
}

export default TextureSwatchPanel
